import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Star, Tag, AlertTriangle, Users, Ticket, LogOut, X, Mail, Calendar, Edit, Trash2, Save, Bell, ShoppingBag, Send, CheckSquare, ImageOff, PackageX, Layers } from 'lucide-react';
import { adminGetStats, adminMe, adminLogout, seedDatabase, adminGetProducts, adminGetCoupons, adminGetCustomers, adminDeleteCustomer, adminGetLowStock, adminUpdateProduct, adminDeleteProduct, adminUpdateCoupon, adminDeleteCoupon, adminGetOrders, adminUpdateOrderStatus, adminGetPushCount, adminSendNotification, adminBulkUpdate, adminGetNoImage } from '../../lib/api';
import toast from 'react-hot-toast';

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [sectionData, setSectionData] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [pushCount, setPushCount] = useState(0);
  const [notifForm, setNotifForm] = useState({ title: '', message: '', url: '/' });
  const [sendingNotif, setSendingNotif] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkProducts, setBulkProducts] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const navigate = useNavigate();

  const refreshStats = async () => {
    try { const res = await adminGetStats(); setStats(res.data); } catch(e) {}
  };

  useEffect(() => {
    const load = async () => {
      try {
        await adminMe();
        await refreshStats();
        try { const pc = await adminGetPushCount(); setPushCount(pc.data.count); } catch(e) {}
      } catch (e) { navigate('/admin/login', { replace: true }); }
      setLoading(false);
    };
    load();
  }, [navigate]);

  const handleLogout = async () => { try { await adminLogout(); } catch(e) {} localStorage.removeItem('motoveloz-admin-token'); navigate('/admin/login', { replace: true }); };

  const handleSeed = async () => {
    setSeeding(true);
    try { const res = await seedDatabase(); toast.success(res.data.message); await refreshStats(); } catch (e) { toast.error('Erro ao popular banco'); }
    setSeeding(false);
  };

  const handleCardClick = async (key) => {
    if (activeSection === key) { setActiveSection(null); setSectionData([]); setEditingItem(null); return; }
    setSectionLoading(true); setActiveSection(key); setEditingItem(null);
    try {
      let data = [];
      switch (key) {
        case 'totalProdutos': data = (await adminGetProducts({ limit: 500 })).data.products || []; break;
        case 'destaques': data = (await adminGetProducts({ destaque: 'true', limit: 500 })).data.products || []; break;
        case 'promocoes': data = (await adminGetProducts({ promocao: 'true', limit: 500 })).data.products || []; break;
        case 'estoqueBaixo': data = (await adminGetLowStock()).data || []; break;
        case 'clientes': data = (await adminGetCustomers()).data || []; break;
        case 'cupons': data = (await adminGetCoupons()).data || []; break;
        case 'pedidos': data = (await adminGetOrders()).data || []; break;
        case 'semFoto': data = (await adminGetNoImage()).data || []; break;
        case 'semEstoque': { const all = (await adminGetProducts({ limit: 500 })).data.products || []; data = all.filter(p => p.estoque <= 0); break; }
        default: break;
      }
      setSectionData(data);
    } catch (e) { toast.error('Erro ao carregar'); setSectionData([]); }
    setSectionLoading(false);
  };

  const handleProductToggle = async (product, field) => {
    try {
      await adminUpdateProduct(product.id, { ...product, [field]: !product[field] });
      toast.success('Atualizado!');
      handleCardClick(activeSection);
      refreshStats();
    } catch (e) { toast.error('Erro'); }
  };

  const handleProductStockSave = async (product, newStock) => {
    try {
      await adminUpdateProduct(product.id, { ...product, estoque: parseInt(newStock) || 0 });
      toast.success('Estoque atualizado!');
      setEditingItem(null);
      handleCardClick(activeSection);
      refreshStats();
    } catch (e) { toast.error('Erro'); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Excluir produto?')) return;
    try { await adminDeleteProduct(id); toast.success('Excluído!'); handleCardClick(activeSection); refreshStats(); } catch (e) { toast.error('Erro'); }
  };

  const handleCouponToggle = async (coupon) => {
    try {
      await adminUpdateCoupon(coupon.id, { ...coupon, ativo: !coupon.ativo });
      toast.success('Atualizado!');
      handleCardClick(activeSection);
      refreshStats();
    } catch (e) { toast.error('Erro'); }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Excluir cupom?')) return;
    try { await adminDeleteCoupon(id); toast.success('Excluído!'); handleCardClick(activeSection); refreshStats(); } catch (e) { toast.error('Erro'); }
  };

  const handleDeleteCustomer = async (userId) => {
    if (!window.confirm('Excluir cliente?')) return;
    try { await adminDeleteCustomer(userId); toast.success('Excluído!'); handleCardClick(activeSection); refreshStats(); } catch (e) { toast.error('Erro'); }
  };

  const handleOrderStatus = async (orderId, status) => {
    try {
      await adminUpdateOrderStatus(orderId, status);
      toast.success(`Pedido ${status}!`);
      handleCardClick(activeSection);
    } catch (e) { toast.error('Erro'); }
  };

  const handleSendNotification = async () => {
    if (!notifForm.title.trim() || !notifForm.message.trim()) { toast.error('Preencha título e mensagem'); return; }
    setSendingNotif(true);
    try {
      const res = await adminSendNotification(notifForm);
      toast.success(`Notificação enviada para ${res.data.sent_to} dispositivo(s)!`);
      setNotifForm({ title: '', message: '', url: '/' });
    } catch (e) { toast.error('Erro ao enviar'); }
    setSendingNotif(false);
  };

  const loadBulkProducts = async () => {
    setBulkLoading(true);
    try { const res = await adminGetProducts({ limit: 500 }); setBulkProducts(res.data.products || []); } catch(e) { toast.error('Erro'); }
    setBulkLoading(false);
  };

  const handleBulkSelectAll = () => {
    if (bulkSelected.length === bulkProducts.length) setBulkSelected([]);
    else setBulkSelected(bulkProducts.map(p => p.id));
  };

  const handleBulkToggle = (id) => {
    setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkApply = async () => {
    if (bulkSelected.length === 0) { toast.error('Selecione produtos'); return; }
    if (!bulkAction) { toast.error('Escolha uma ação'); return; }
    let value = true;
    if (bulkAction === 'set_estoque') value = parseInt(bulkValue) || 0;
    else if (bulkAction.includes('remove')) value = false;
    const actionMap = { add_destaque: 'set_destaque', remove_destaque: 'set_destaque', add_promocao: 'set_promocao', remove_promocao: 'set_promocao', set_estoque: 'set_estoque' };
    const valueMap = { add_destaque: true, remove_destaque: false, add_promocao: true, remove_promocao: false, set_estoque: parseInt(bulkValue) || 0 };
    try {
      const res = await adminBulkUpdate({ product_ids: bulkSelected, action: actionMap[bulkAction], value: valueMap[bulkAction] });
      toast.success(`${res.data.modified} produtos atualizados!`);
      setBulkSelected([]);
      refreshStats();
      loadBulkProducts();
    } catch(e) { toast.error('Erro na edição em massa'); }
  };

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center"><div className="animate-pulse text-zinc-500">Carregando...</div></div>;

  const statCards = [
    { key: 'totalProdutos', title: 'Total Produtos', value: stats?.totalProdutos, icon: Package, color: 'text-blue-500', bg: 'bg-blue-500/20' },
    { key: 'destaques', title: 'Destaques', value: stats?.produtosDestaque, icon: Star, color: 'text-yellow-500', bg: 'bg-yellow-500/20' },
    { key: 'promocoes', title: 'Promoções', value: stats?.produtosPromocao, icon: Tag, color: 'text-green-500', bg: 'bg-green-500/20' },
    { key: 'estoqueBaixo', title: 'Estoque Baixo', value: stats?.produtosEstoqueBaixo, icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/20' },
    { key: 'clientes', title: 'Clientes', value: stats?.totalClientes, icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/20' },
    { key: 'cupons', title: 'Cupons Ativos', value: stats?.totalCupons, icon: Ticket, color: 'text-cyan-500', bg: 'bg-cyan-500/20' },
  ];

  const sectionTitles = {
    totalProdutos: 'Todos os Produtos', destaques: 'Produtos em Destaque', promocoes: 'Produtos em Promoção',
    estoqueBaixo: 'Produtos com Estoque Baixo', clientes: 'Clientes Cadastrados', cupons: 'Cupons Ativos', pedidos: 'Histórico de Pedidos',
    semFoto: 'Produtos Sem Foto', semEstoque: 'Produtos Sem Estoque',
  };

  const statusColors = { pendente: 'bg-yellow-600', confirmado: 'bg-blue-600', enviado: 'bg-purple-600', entregue: 'bg-green-600', cancelado: 'bg-red-600' };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2"><img src="/motoveloz_logo_semfundo.png" alt="MotoVeloz" className="w-10 h-10 object-contain" /><span className="text-white font-bold uppercase text-lg" style={{ fontFamily: 'Teko, sans-serif' }}>Admin</span></Link>
            <nav className="hidden md:flex gap-4">
              <Link to="/admin/dashboard" className="text-red-500 font-bold text-sm uppercase tracking-wider">Dashboard</Link>
              <Link to="/admin/produtos" className="text-zinc-400 hover:text-white font-bold text-sm uppercase tracking-wider">Produtos</Link>
              <Link to="/admin/cupons" className="text-zinc-400 hover:text-white font-bold text-sm uppercase tracking-wider">Cupons</Link>
              <button onClick={() => handleCardClick('pedidos')} className="text-zinc-400 hover:text-white font-bold text-sm uppercase tracking-wider">Pedidos</button>
              <button onClick={() => setShowBulkEdit(true)} className="text-zinc-400 hover:text-white font-bold text-sm uppercase tracking-wider flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Edição em Massa</button>
            </nav>
          </div>
          <button onClick={handleLogout} className="text-zinc-400 hover:text-red-500 flex items-center gap-2 text-sm" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Sair</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Teko, sans-serif' }} data-testid="admin-dashboard-title">Dashboard</h1>
            <p className="text-zinc-500 text-sm">Painel administrativo MOTOVELOZ</p>
          </div>
          <button onClick={handleSeed} disabled={seeding} data-testid="seed-btn"
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 font-bold text-sm uppercase tracking-wider transition-colors">
            {seeding ? 'Populando...' : 'Popular Banco'}
          </button>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          {statCards.map((s) => {
            const Icon = s.icon;
            const isActive = activeSection === s.key;
            return (
              <button key={s.key} onClick={() => handleCardClick(s.key)}
                className={`bg-zinc-900 border p-4 transition-all text-left cursor-pointer ${isActive ? 'border-red-600 ring-1 ring-red-600' : 'border-zinc-800 hover:border-red-600'}`}
                data-testid={`stat-${s.title}`}>
                <div className={`p-2 rounded-lg ${s.bg} w-fit mb-3`}><Icon className={`h-5 w-5 ${s.color}`} /></div>
                <p className="text-zinc-500 text-xs uppercase tracking-wider">{s.title}</p>
                <p className="text-white text-2xl font-bold">{s.value ?? 0}</p>
              </button>
            );
          })}
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button onClick={() => handleCardClick('pedidos')} data-testid="btn-orders"
            className={`flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase tracking-wider transition-all ${activeSection === 'pedidos' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white border border-zinc-700'}`}>
            <ShoppingBag className="h-4 w-4" /> Pedidos
          </button>
          <button onClick={() => handleCardClick('semFoto')} data-testid="btn-no-image"
            className={`flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase tracking-wider transition-all ${activeSection === 'semFoto' ? 'bg-orange-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white border border-zinc-700'}`}>
            <ImageOff className="h-4 w-4" /> Sem Foto
          </button>
          <button onClick={() => handleCardClick('semEstoque')} data-testid="btn-no-stock"
            className={`flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase tracking-wider transition-all ${activeSection === 'semEstoque' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white border border-zinc-700'}`}>
            <PackageX className="h-4 w-4" /> Sem Estoque
          </button>
          <button onClick={() => { setShowBulkEdit(true); loadBulkProducts(); }} data-testid="btn-bulk-edit"
            className="flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase tracking-wider bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white border border-zinc-700 transition-all">
            <Layers className="h-4 w-4" /> Edição em Massa
          </button>
        </div>

        {/* Expandable Section */}
        {activeSection && (
          <div className="bg-zinc-900 border border-zinc-800 p-6 mb-8" data-testid="dashboard-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Teko, sans-serif' }}>{sectionTitles[activeSection]}</h2>
              <button onClick={() => { setActiveSection(null); setSectionData([]); setEditingItem(null); }} className="text-zinc-500 hover:text-white p-1" data-testid="close-section-btn"><X className="h-5 w-5" /></button>
            </div>

            {sectionLoading ? (
              <div className="text-center py-8"><p className="text-zinc-500 animate-pulse">Carregando...</p></div>
            ) : activeSection === 'clientes' ? (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {sectionData.length === 0 ? <p className="text-zinc-500 text-center py-4">Nenhum cliente cadastrado</p> : sectionData.map((c, i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 p-3 flex items-center gap-4">
                    {c.picture ? <img src={c.picture} alt="" className="w-10 h-10 rounded-full" /> : <Users className="h-10 w-10 text-zinc-600 p-2 bg-zinc-700 rounded-full" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate">{c.name || 'Sem nome'}</p>
                      <p className="text-zinc-500 text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-zinc-500 text-[10px] flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '-'}</p>
                      <button onClick={() => handleDeleteCustomer(c.user_id)} className="text-red-500 hover:text-red-400 p-1" title="Excluir"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : activeSection === 'cupons' ? (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {sectionData.length === 0 ? <p className="text-zinc-500 text-center py-4">Nenhum cupom</p> : sectionData.map((c, i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 p-3 flex items-center justify-between">
                    <div>
                      <p translate="no" className="text-white font-bold text-sm">{c.code}</p>
                      <p className="text-zinc-500 text-xs">{c.tipo === 'porcentagem' ? `${c.valor}% desconto` : 'Frete Grátis'} | Min: R$ {c.minCompra?.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-zinc-500 text-xs">Usos: {c.usosCount}/{c.maxUsos}</p>
                      </div>
                      <button onClick={() => handleCouponToggle(c)} className={`text-[10px] px-2 py-1 font-bold cursor-pointer ${c.ativo ? 'bg-green-600 text-white hover:bg-red-600' : 'bg-zinc-700 text-zinc-400 hover:bg-green-600 hover:text-white'}`}>{c.ativo ? 'Ativo' : 'Inativo'}</button>
                      <Link to="/admin/cupons" className="text-yellow-500 hover:text-yellow-400 p-1" title="Editar"><Edit className="h-4 w-4" /></Link>
                      <button onClick={() => handleDeleteCoupon(c.id)} className="text-red-500 hover:text-red-400 p-1" title="Excluir"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : activeSection === 'pedidos' ? (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {sectionData.length === 0 ? <p className="text-zinc-500 text-center py-4">Nenhum pedido registrado</p> : sectionData.map((o, i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-white font-bold text-sm">Pedido #{o.id?.slice(-8)}</p>
                        <p className="text-zinc-500 text-xs">{o.user_name || 'Cliente'} | {new Date(o.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-1 font-bold text-white uppercase ${statusColors[o.status] || 'bg-zinc-600'}`}>{o.status}</span>
                        <select value={o.status} onChange={(e) => handleOrderStatus(o.id, e.target.value)}
                          className="bg-zinc-700 text-white text-xs border border-zinc-600 px-2 py-1">
                          <option value="pendente">Pendente</option>
                          <option value="confirmado">Confirmado</option>
                          <option value="enviado">Enviado</option>
                          <option value="entregue">Entregue</option>
                          <option value="cancelado">Cancelado</option>
                        </select>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-400 space-y-0.5">
                      {o.items?.map((item, j) => (
                        <p key={j}>{item.quantidade}x {item.nome} - R$ {(item.preco * item.quantidade).toFixed(2)}</p>
                      ))}
                    </div>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className="text-zinc-500">Entrega: <span className="text-white">{o.tipo_entrega === 'retirar' ? 'Retirar' : 'Domicílio'}</span></span>
                      <span className="text-zinc-500">Total: <span className="text-red-500 font-bold">R$ {o.total?.toFixed(2)}</span></span>
                      {o.distancia_km && <span className="text-zinc-500">Dist: <span className="text-white">{o.distancia_km} km</span></span>}
                    </div>
                    {o.client_lat && (
                      <a href={`https://www.google.com/maps?q=${o.client_lat},${o.client_lng}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs mt-1 block">Ver localização do cliente</a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Products list */
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {sectionData.length === 0 ? <p className="text-zinc-500 text-center py-4">Nenhum produto encontrado</p> : sectionData.map((p, i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 p-3 flex gap-3 items-center">
                    <div className="w-12 h-12 bg-zinc-700 flex-shrink-0 overflow-hidden">
                      {p.imagemUrl ? <img src={p.imagemUrl} alt="" className="w-full h-full object-cover" /> : <Package className="h-6 w-6 text-zinc-600 m-auto mt-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p translate="no" className="text-white font-bold text-sm truncate">{p.nome}</p>
                      <p className="text-zinc-500 text-xs">Cód: {p.codigo} | {p.marca} | {p.categoria}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingItem === p.id ? (
                        <div className="flex items-center gap-1">
                          <input type="number" defaultValue={p.estoque} id={`stock-${p.id}`} className="w-16 px-2 py-1 bg-zinc-900 border border-zinc-600 text-white text-xs" />
                          <button onClick={() => handleProductStockSave(p, document.getElementById(`stock-${p.id}`).value)} className="text-green-500 hover:text-green-400 p-1"><Save className="h-4 w-4" /></button>
                          <button onClick={() => setEditingItem(null)} className="text-zinc-500 hover:text-white p-1"><X className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <div className="text-right">
                          <p className="text-red-500 font-bold text-sm">R$ {p.precoVenda?.toFixed(2)}</p>
                          <p className={`text-xs font-bold ${p.estoque > 5 ? 'text-green-500' : p.estoque > 0 ? 'text-yellow-500' : 'text-red-500'}`}>Est: {p.estoque}</p>
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        {(activeSection === 'destaques' || activeSection === 'totalProdutos') && (
                          <button onClick={() => handleProductToggle(p, 'isDestaque')} title={p.isDestaque ? 'Remover destaque' : 'Destacar'}
                            className={`p-1 ${p.isDestaque ? 'text-yellow-500' : 'text-zinc-600 hover:text-yellow-500'}`}><Star className={`h-3.5 w-3.5 ${p.isDestaque ? 'fill-current' : ''}`} /></button>
                        )}
                        {(activeSection === 'promocoes' || activeSection === 'totalProdutos') && (
                          <button onClick={() => handleProductToggle(p, 'isPromocao')} title={p.isPromocao ? 'Remover promoção' : 'Promover'}
                            className={`p-1 ${p.isPromocao ? 'text-green-500' : 'text-zinc-600 hover:text-green-500'}`}><Tag className={`h-3.5 w-3.5 ${p.isPromocao ? 'fill-current' : ''}`} /></button>
                        )}
                      </div>
                      <button onClick={() => setEditingItem(p.id)} className="text-yellow-500 hover:text-yellow-400 p-1" title="Editar estoque"><Edit className="h-4 w-4" /></button>
                      <button onClick={() => handleDeleteProduct(p.id)} className="text-red-500 hover:text-red-400 p-1" title="Excluir"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-zinc-600 text-xs mt-3">{sectionData.length} registro{sectionData.length !== 1 ? 's' : ''}</p>
          </div>
        )}

        {/* Push Notifications */}
        <div className="bg-zinc-900 border border-zinc-800 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white uppercase tracking-tight flex items-center gap-2" style={{ fontFamily: 'Teko, sans-serif' }}><Bell className="h-5 w-5 text-yellow-500" /> Notificações Push</h2>
            <span className="text-zinc-500 text-xs">{pushCount} inscrito{pushCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input type="text" placeholder="Título da notificação" value={notifForm.title} onChange={(e) => setNotifForm(prev => ({ ...prev, title: e.target.value }))}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:border-red-500" data-testid="notif-title" />
            <input type="text" placeholder="Mensagem" value={notifForm.message} onChange={(e) => setNotifForm(prev => ({ ...prev, message: e.target.value }))}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:border-red-500" data-testid="notif-message" />
            <input type="text" placeholder="URL (ex: /produtos?promocao=true)" value={notifForm.url} onChange={(e) => setNotifForm(prev => ({ ...prev, url: e.target.value }))}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:border-red-500" />
          </div>
          <button onClick={handleSendNotification} disabled={sendingNotif} data-testid="send-notif-btn"
            className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white px-6 py-2 font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Send className="h-4 w-4" /> {sendingNotif ? 'Enviando...' : 'Enviar Notificação'}
          </button>
        </div>

        {/* Quick Actions */}
        <div className="bg-zinc-900 border border-zinc-800 p-6">
          <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Teko, sans-serif' }}>Ações Rápidas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link to="/admin/produtos" className="bg-zinc-800 hover:bg-red-600 text-white p-4 font-bold text-center transition-all border border-zinc-700 hover:border-red-500 text-sm uppercase tracking-wider" data-testid="quick-manage-products">Gerenciar Produtos</Link>
            <Link to="/admin/cupons" className="bg-zinc-800 hover:bg-red-600 text-white p-4 font-bold text-center transition-all border border-zinc-700 hover:border-red-500 text-sm uppercase tracking-wider" data-testid="quick-manage-coupons">Gerenciar Cupons</Link>
            <Link to="/" className="bg-zinc-800 hover:bg-red-600 text-white p-4 font-bold text-center transition-all border border-zinc-700 hover:border-red-500 text-sm uppercase tracking-wider">Ver Loja</Link>
          </div>
        </div>

        {/* Bulk Edit Modal */}
        {showBulkEdit && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkEdit(false)}>
            <div className="bg-zinc-900 border border-zinc-700 w-full max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <h2 className="text-xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Teko, sans-serif' }}>Edição em Massa</h2>
                <button onClick={() => setShowBulkEdit(false)} className="text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
              </div>

              {/* Action bar */}
              <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-3 items-center">
                <button onClick={handleBulkSelectAll} className="text-xs px-3 py-1.5 border border-zinc-600 text-zinc-300 hover:bg-zinc-700 flex items-center gap-1">
                  <CheckSquare className="h-3.5 w-3.5" /> {bulkSelected.length === bulkProducts.length ? 'Desmarcar todos' : 'Selecionar todos'}
                </button>
                <span className="text-zinc-500 text-xs">{bulkSelected.length} selecionado(s)</span>
                <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}
                  className="bg-zinc-800 border border-zinc-600 text-white text-xs px-3 py-1.5" data-testid="bulk-action-select">
                  <option value="">Escolha a ação...</option>
                  <option value="add_destaque">Marcar como Destaque</option>
                  <option value="remove_destaque">Remover Destaque</option>
                  <option value="add_promocao">Marcar como Promoção</option>
                  <option value="remove_promocao">Remover Promoção</option>
                  <option value="set_estoque">Definir Estoque</option>
                </select>
                {bulkAction === 'set_estoque' && (
                  <input type="number" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Qtd"
                    className="w-20 bg-zinc-800 border border-zinc-600 text-white text-xs px-2 py-1.5" />
                )}
                <button onClick={handleBulkApply} disabled={bulkSelected.length === 0 || !bulkAction}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs px-4 py-1.5 font-bold uppercase" data-testid="bulk-apply-btn">
                  Aplicar
                </button>
              </div>

              {/* Products list */}
              <div className="flex-1 overflow-y-auto p-4">
                {bulkLoading ? <p className="text-zinc-500 text-center py-8 animate-pulse">Carregando produtos...</p> : (
                  <div className="space-y-1">
                    {bulkProducts.map((p) => (
                      <label key={p.id} className={`flex items-center gap-3 p-2 cursor-pointer transition-all ${bulkSelected.includes(p.id) ? 'bg-red-600/10 border border-red-600/50' : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'}`}>
                        <input type="checkbox" checked={bulkSelected.includes(p.id)} onChange={() => handleBulkToggle(p.id)}
                          className="accent-red-500 w-4 h-4" />
                        <div className="w-8 h-8 bg-zinc-700 flex-shrink-0 overflow-hidden">
                          {p.imagemUrl ? <img src={p.imagemUrl} alt="" className="w-full h-full object-cover" /> : <Package className="h-4 w-4 text-zinc-600 m-auto mt-2" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold truncate">{p.nome}</p>
                          <p className="text-zinc-500 text-[10px]">Cód: {p.codigo} | Est: {p.estoque} | R$ {p.precoVenda?.toFixed(2)}</p>
                        </div>
                        <div className="flex gap-1">
                          {p.isDestaque && <span className="text-[9px] px-1.5 py-0.5 bg-yellow-600/30 text-yellow-400 font-bold">DEST</span>}
                          {p.isPromocao && <span className="text-[9px] px-1.5 py-0.5 bg-green-600/30 text-green-400 font-bold">PROMO</span>}
                          {!p.imagemUrl && <span className="text-[9px] px-1.5 py-0.5 bg-orange-600/30 text-orange-400 font-bold">SEM FOTO</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
